import React, { useState, useEffect, useRef } from "react";
import LoggedInHeader from "../components/LoggedInHeader";
import { Glossary } from "../components/Glossary";
import glossaryTerms from "../data/GlossaryTerms";
import StocksAboveMAWidget from "../components/StocksAboveMAWidget";

import { AlertTriangle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import "../assets/styles/Strategies.css";
import ReactTooltip from "react-tooltip";
import useIsMobile from "../hooks/useIsMobile";

const Strategies = () => {
  const isMobile = useIsMobile();
  // State to track the active section
  const [activeSection, setActiveSection] = useState("intro");
  // State to track if TOC is minimized
  const [isTocMinimized, setIsTocMinimized] = useState(false);
  // Mobile-only: TOC renders as a collapsible accordion under the header
  const [isTocOpen, setIsTocOpen] = useState(false);
  // State to track collapsed sections
  const [collapsedSections, setCollapsedSections] = useState({
    strategies: true,
    investments: false,
    orderTypes: true,
    buyLowSellHigh: true,
  });
  
  // Investment recommendations data
  const investments = [
    {
      ticker: "VOO",
      name: "Vanguard S&P 500 ETF",
      expense: "0.03%",
      description: "Owns shares in 500 of America's largest companies like Apple, Microsoft, and Amazon"
    },
    {
      ticker: "VT",
      name: "Vanguard Total World Stock ETF",
      expense: "0.07%",
      description: "Owns pieces of nearly every public company on Earth - over 9,000 companies worldwide"
    },
    {
      ticker: "QQQ",
      name: "Invesco QQQ ETF",
      expense: "0.20%",
      description: "Focuses on 100 of the largest non-financial companies, with a heavy focus on technology"
    },
    {
      ticker: "BRK.B",
      name: "Berkshire Hathaway (Class B)",
      expense: "0.00%",
      description: "Warren Buffett's company that owns many businesses and invests in other companies"
    },
    {
      ticker: "VGTSX",
      name: "Vanguard Total International Stock Index",
      expense: "0.17%",
      description: "Owns companies from developed and emerging markets outside the U.S."
    },
    {
      ticker: "MUB",
      name: "iShares National Muni Bond ETF",
      expense: "0.07%",
      description: "Holds tax-exempt bonds issued by states, cities, and counties across the U.S."
    },
    {
      ticker: "BND",
      name: "Vanguard Total Bond Market ETF",
      expense: "0.03%",
      description: "Holds thousands of U.S. government and corporate bonds of different lengths"
    },
    {
      ticker: "FBTC",
      name: "Fidelity Wise Origin Bitcoin ETF",
      expense: "0.25%",
      description: "Tracks the price of Bitcoin without needing a crypto wallet"
    },
    {
      ticker: "FETH",
      name: "Fidelity Ethereum ETF",
      expense: "0.25%",
      description: "Tracks the price of Ethereum without needing a crypto wallet"
    },
    {
      ticker: "N/A",
      name: "Robinhood Gold 4% APY",
      expense: "$5/month",
      description: "Cash management account paying 4% interest on uninvested cash (requires Gold subscription)"
    }
  ];
  
  const strategies = [
    {
      id: 1,
      title: "Conservative Strategy",
      pros: [
        "Safer - less ups and downs",
        "Steadier account balance",
        "Regular income from safe investments",
        "Keeps cash ready for buying opportunities"
      ],
      cons: [
        "Makes less money when stocks do well",
        "Might not keep up with inflation",
        "Interest-based investments can lose value when rates change",
        "Too cautious if you're young"
      ],
      explanation: "Think of this like driving slowly on a bumpy road. You won't go as fast, but the ride is smoother. You put most money in 'risk-free' options (like high-yield savings, Robinhood Gold APY, or government bonds) and some in big company stocks. Perfect if you're close to retirement or hate seeing your money drop in value.",
      renderExplanation: () => (
        <>
          Think of this like driving slowly on a bumpy road. You won't go as fast, but the ride is smoother. You put most money in <Glossary term="Risk-Free">risk-free</Glossary> options (like <Glossary term="High Yield Savings" definition={glossaryTerms["High Yield Savings"]}>high-yield savings</Glossary>, Robinhood Gold APY, or government <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary>) and some in big company <Glossary term="Stock" definition={glossaryTerms["Stock"]}>stocks</Glossary>. Perfect if you're close to retirement or hate seeing your money drop in value.
        </>
      ),
      image: "conservative-strategy.PNG"
    },
    {
      id: 2,
      title: "Simple Strategy",
      pros: [
        "Better growth potential than conservative",
        "Keeps up with rising prices",
        "Proven to work well over time",
        "Still has some safety net with risk-free options",
        "Good for 10+ year timeframes"
      ],
      cons: [
        "More ups and downs than conservative",
        "Bigger drops during market crashes",
        "Less regular income",
        "Need to be okay with value changing"
      ],
      explanation: "This is like a car driving at medium speed - fast enough to get somewhere, but not so fast it's dangerous. 70% in stocks (growth) and 30% in risk-free options (high-yield savings, Robinhood Gold APY, or bonds). Perfect for most people in their 30s-40s who are saving for retirement. It's balanced enough to grow but not so risky you'll panic when markets drop.",
      renderExplanation: () => (
        <>
          This is like a car driving at medium speed - fast enough to get somewhere, but not so fast it's dangerous. 70% in <Glossary term="Stock" definition={glossaryTerms["Stock"]}>stocks</Glossary> (growth) and 30% in <Glossary term="Risk-Free">risk-free options</Glossary> (<Glossary term="High Yield Savings" definition={glossaryTerms["High Yield Savings"]}>high-yield savings</Glossary>, Robinhood Gold APY, or <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary>). Perfect for most people in their 30s-40s who are saving for retirement. It's balanced enough to grow but not so risky you'll panic when <Glossary term="Market Crash" definition={glossaryTerms["Market Crash"]}>markets crash</Glossary>.
        </>
      ),
      image: "simple-strategy.PNG"
    },
    {
      id: 3,
      title: "Three Fund Strategy",
      pros: [
        "Invests worldwide, not just USA",
        "When one area struggles, others might do well",
        "Simple but smart approach",
        "Owns thousands of companies",
        "Keeps costs low"
      ],
      cons: [
        "Foreign investments affected by currency changes",
        "US stocks sometimes do better",
        "Needs occasional rebalancing",
        <><Glossary term="Risk-Free" definition={glossaryTerms["Risk-Free"]}>Risk-free options</Glossary> may not keep up with <Glossary term="Inflation" definition={glossaryTerms["Inflation"]}>inflation</Glossary></>,
        <>International stocks can be <Glossary term="Volatile" definition={glossaryTerms["Volatile"]}>volatile</Glossary></>
      ],
      renderCons: () => [
        "Foreign investments affected by currency changes",
        "US stocks sometimes do better",
        "Needs occasional rebalancing",
        <><Glossary term="Risk-Free" definition={glossaryTerms["Risk-Free"]}>Risk-free options</Glossary> may not keep up with <Glossary term="Inflation" definition={glossaryTerms["Inflation"]}>inflation</Glossary></>,
        <>International stocks can be <Glossary term="Volatile" definition={glossaryTerms["Volatile"]}>volatile</Glossary></>
      ],
      explanation: "Imagine spreading your seeds in three different gardens instead of one. If pests hit one garden, the others might still grow well. You divide money equally between risk-free options (like high-yield savings or bonds), US stocks (growth), and international stocks (growth from other countries). It's like not putting all your eggs in one basket.",
      renderExplanation: () => (
        <>
          Imagine spreading your seeds in three different gardens instead of one. If pests hit one garden, the others might still grow well. You divide money equally between <Glossary term="Risk-Free" definition={glossaryTerms["Risk-Free"]}>risk-free options</Glossary> (like <Glossary term="High Yield Savings" definition={glossaryTerms["High Yield Savings"]}>high-yield savings</Glossary> or <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary>), US <Glossary term="Stock" definition={glossaryTerms["Stock"]}>stocks</Glossary> (growth), and international stocks (growth from other countries). It's like not putting all your eggs in one basket - this is called <Glossary term="Diversification" definition={glossaryTerms["Diversification"]}>diversification</Glossary>.
        </>
      ),
      image: "three-fund-portfolio.PNG"
    },
    {
      id: 4,
      title: "Finn Tech Strategy",
      pros: [
        "Better growth potential through tech focus",
        "Mix of established companies and new tech",
        "Includes Warren Buffett's company picks",
        "Small stake in digital currencies",
        "Focus on innovation"
      ],
      cons: [
        "Big price swings",
        "Tech stocks often fall together",
        "Large bet on one company",
        "Crypto can crash dramatically",
        "No safety net from risk-free investments"
      ],
      renderCons: () => [
        <>Big price <Glossary term="Volatility" definition={glossaryTerms["Volatility"]}>swings</Glossary></>,
        "Tech stocks often fall together",
        "Large bet on one company",
        <><Glossary term="Cryptocurrency" definition={glossaryTerms["Cryptocurrency"]}>Crypto</Glossary> can crash dramatically</>,
        <>No safety net from <Glossary term="Risk-Free" definition={glossaryTerms["Risk-Free"]}>risk-free investments</Glossary></>
      ],
      explanation: "This is like driving a sports car - thrilling but risky. You invest in big US companies, tech companies, Warren Buffett's picks, and a small amount in digital currencies. No money in high-yield savings or bonds. Picture a recipe with some traditional ingredients and some experimental ones. Best for younger people who won't need the money for 10+ years and can stomach big drops without panicking.",
      renderExplanation: () => (
        <>
          This is like driving a sports car - thrilling but risky. You invest in big US companies, tech companies, <Glossary term="Warren Buffett" definition={glossaryTerms["Warren Buffett"]}>Warren Buffett's</Glossary> picks, and a small amount in <Glossary term="Cryptocurrency" definition={glossaryTerms["Cryptocurrency"]}>digital currencies</Glossary>. No money in <Glossary term="High Yield Savings" definition={glossaryTerms["High Yield Savings"]}>high-yield savings</Glossary> or <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary>. Picture a recipe with some traditional ingredients and some experimental ones. Best for younger people who won't need the money for 10+ years and can stomach <Glossary term="Volatility" definition={glossaryTerms["Volatility"]}>big price swings</Glossary> without panicking.
        </>
      ),
      image: "finn-tech-portfolio.PNG"
    },
    {
      id: 5,
      title: "\"VT and Chill\" Strategy",
      pros: [
        "Ultra simple - just one fund",
        "Owns nearly every company worldwide",
        "Very low fees",
        "No maintenance needed",
        "History shows markets grow long-term"
      ],
      cons: [
        "Big ups and downs with no buffer",
        "Dramatic drops during crashes",
        "Hard to stick with during downturns",
        "Can't adjust risk level",
        "Needs 15+ years to work best"
      ],
      renderCons: () => [
        "Big ups and downs with no buffer",
        <>Dramatic drops during <Glossary term="Bear Market" definition={glossaryTerms["Bear Market"]}>crashes</Glossary></>,
        "Hard to stick with during downturns",
        "Can't adjust risk level",
        "Needs 15+ years to work best"
      ],
      explanation: "Imagine planting a seed in every field around the world and then just waiting. You buy one fund (VT) that owns tiny pieces of almost every company on Earth. No high-yield savings or bonds to smooth the ride. Then you do nothing except wait - like planting a tree and letting it grow. Perfect for people who want the simplest approach possible and can wait many years through good and bad times without panicking.",
      renderExplanation: () => (
        <>
          Imagine planting a seed in every field around the world and then just waiting. You buy one <Glossary term="ETF" definition={glossaryTerms["ETF"]}>fund</Glossary> (VT) that owns tiny pieces of almost every company on Earth. No <Glossary term="High Yield Savings" definition={glossaryTerms["High Yield Savings"]}>high-yield savings</Glossary> or <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary> to smooth the ride. Then you do nothing except wait - like planting a tree and letting it grow. Perfect for people who want the simplest approach possible and can wait many years through <Glossary term="Bull Market" definition={glossaryTerms["Bull Market"]}>good</Glossary> and <Glossary term="Bear Market" definition={glossaryTerms["Bear Market"]}>bad</Glossary> times without panicking.
        </>
      ),
      image: "vt-and-chill.PNG"
    }
  ];
  
  // Refs for each section to enable scrolling
  const sectionRefs = useRef({
    intro: null,
    investments: null,
    rebalancing: null,
    orderTypes: null,
    marketOrder: null,
    limitOrder: null,
    stopLimitOrder: null,
    buyLowSellHigh: null,
    rsiIndicator: null,
    stocksAboveMA: null,
    standardDeviation: null,
    dollarCostAveraging: null,
  });
  
  // Initialize strategy refs
  strategies.forEach(strategy => {
    sectionRefs.current[`strategy-${strategy.id}`] = null;
  });
  
  // Handle scrolling to a section when clicking on a TOC item
  const scrollToSection = (sectionId) => {
    const section = sectionRefs.current[sectionId];
    if (section) {
      setIsTocOpen(false);
      // The accordion collapse shifts content up, so read offsetTop only
      // after the re-render has been flushed (state updates in event
      // handlers are committed before timeouts run)
      setTimeout(() => {
        window.scrollTo({
          top: section.offsetTop - 100,
          behavior: 'smooth'
        });
      }, 0);
      setActiveSection(sectionId);
    }
  };
  
  // Toggle TOC minimized state
  const toggleTocMinimized = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setIsTocMinimized(!isTocMinimized);
  };
  
  // Toggle section collapsed state
  const toggleSectionCollapsed = (e, section) => {
    e.stopPropagation(); // Prevent event bubbling
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Track scroll position and update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Adding offset for header
      
      // Find the section that is currently visible
      let currentSection = "intro";
      Object.keys(sectionRefs.current).forEach((sectionId) => {
        const section = sectionRefs.current[sectionId];
        if (section && section.offsetTop <= scrollPosition) {
          currentSection = sectionId;
        }
      });
      
      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // Set up refs after initial render
  useEffect(() => {
    // Force update active section after initial render
    const timer = setTimeout(() => {
      const scrollPosition = window.scrollY + 150;
      let currentSection = "intro";
      
      Object.keys(sectionRefs.current).forEach((sectionId) => {
        const section = sectionRefs.current[sectionId];
        if (section && section.offsetTop <= scrollPosition) {
          currentSection = sectionId;
        }
      });
      
      setActiveSection(currentSection);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const RebalancingExplanation = () => (
    <div ref={el => sectionRefs.current['rebalancing'] = el} className="strategy-section">
      <h1 id="rebalancing" className="strategy-header">Rebalancing Your <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>Portfolio</Glossary></h1>
      
      <h3>What is <Glossary term="Rebalancing">Rebalancing</Glossary>?</h3>
      <p>
        <Glossary term="Rebalancing">Rebalancing</Glossary> is the process of realigning your investments back to your target <Glossary term="Allocation" definition={glossaryTerms["Allocation"]}>allocation</Glossary>. 
        Over time, some investments will grow faster than others, causing your <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>portfolio</Glossary> to drift from your original plan.
      </p>
      
      <h3>Why Rebalance?</h3>
      <p>
        Rebalancing helps manage risk. For example, if your <Glossary term="Stock">stocks</Glossary> perform exceptionally well and grow from 60% to 80% of your <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>portfolio</Glossary>, 
        you now have more exposure to stock market risk than you originally intended. Rebalancing brings this back in line with your risk tolerance.
      </p>
      
      <h3>Simple Example</h3>
      <p>
        Let's say you start with $10,000 invested as:
        <ul>
          <li>60% <Glossary term="Stock">Stocks</Glossary>: $6,000</li>
          <li>40% <Glossary term="Bond">Bonds</Glossary> and <Glossary term="High Yield Savings">high-yield savings</Glossary> (more <Glossary term="Risk-Free">risk-free options</Glossary>): $4,000</li>
        </ul>
        
        After a year, your <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>portfolio</Glossary> might look like:
        <ul>
          <li>Stocks: $7,500 (now 65.2% of your <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>portfolio</Glossary>)</li>
          <li>Bonds/Savings: $4,000 (now 34.8% of your <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>portfolio</Glossary>)</li>
        </ul>
        
        To rebalance, you would sell $600 of stocks and buy $600 of bonds to return to your 60/40 split.
      </p>
      
      <h3>When to Rebalance</h3>
      <p>
        Common approaches include:
        <ul>
          <li><strong>Calendar-based:</strong> Rebalance every 6 or 12 months</li>
          <li><strong>Threshold-based:</strong> Rebalance when an <Glossary term="Asset Class" definition={glossaryTerms["Asset Class"]}>asset class</Glossary> drifts more than 5% from its target</li>
          <li><strong>Combination:</strong> Check quarterly, but only rebalance if drift exceeds 5%</li>
        </ul>
      </p>
      
      <p className="note">
        <strong>Tax Consideration:</strong> In <Glossary term="Taxable Account" definition={glossaryTerms["Taxable Account"]}>taxable accounts</Glossary>, rebalancing can trigger <Glossary term="Capital Gains Tax" definition={glossaryTerms["Capital Gains Tax"]}>capital gains taxes</Glossary>. Consider using new contributions to rebalance or rebalance within <Glossary term="Tax-Advantaged Account" definition={glossaryTerms["Tax-Advantaged Account"]}>tax-advantaged accounts</Glossary> when possible.
      </p>
    </div>
  );

  const BuyLowSellHighSection = () => (
    
    <div ref={el => sectionRefs.current.buyLowSellHigh = el} className="strategy-section">
      <hr />
      <h1 className="strategy-header">Buy Low, Sell High</h1>
      
      <div className="quote-box">
        <blockquote>
          "Be fearful when others are greedy, and greedy when others are fearful."
          <cite>- Warren Buffett</cite>
        </blockquote>
      </div>
      
      <p className="section-intro">
        While most financial advisors recommend a "buy and hold" approach for average investors, understanding market bottoms 
        can help you make smarter investing decisions during market downturns. Here are some simplified ways to identify potential buying opportunities.
      </p>
      
      <div className="market-timing-section">
        <h3>What is a <Glossary term="Market Bottom" definition={glossaryTerms["Market Bottom"]}>Market Bottom</Glossary>?</h3>
        <p>
          Think of a market bottom like the lowest point of a bouncing ball before it starts to rise again. It's when stock prices 
          stop falling and begin to recover. Identifying this point before it happens would be incredibly valuable—but it's also 
          extremely difficult.
        </p>
        <div className="warning-notice">
          <AlertTriangle size={20} />
          <p>
            <strong>Important:</strong> Perfect <Glossary term="Market Timing" definition={glossaryTerms["Market Timing"]}>market timing</Glossary> is nearly impossible. 
            Even professional investors struggle to consistently identify market bottoms. These tools should supplement—not replace—a 
            long-term investment strategy.
          </p>
        </div>
      </div>
      
      <div className="timing-indicators">
        <h3>Tools to Help Identify Potential Buying Opportunities</h3>
        
        <div 
          ref={el => sectionRefs.current.rsiIndicator = el}
          className="indicator-box"
        >
          <h4><Glossary term="RSI" definition={glossaryTerms["RSI"]}>RSI</Glossary> (Relative Strength Index)</h4>
          <p>
            Think of RSI like a speedometer for the market that measures how fast prices are changing. It gives readings from 0 to 100:
          </p>
          <ul>
            <li><strong>Below 30:</strong> Like a car that's slowed down too much—the market might be <Glossary term="Oversold" definition={glossaryTerms["Oversold"]}>"oversold"</Glossary> and due for a bounce</li>
            <li><strong>Above 70:</strong> Like a car that's going too fast—the market might be <Glossary term="Overbought" definition={glossaryTerms["Overbought"]}>"overbought"</Glossary> and due for a slowdown</li>
            <li><strong>Between 30-70:</strong> Cruising at a moderate pace—nothing unusual</li>
          </ul>
          <p>
            When the RSI drops below 30 and then starts to rise again, it might signal a good buying opportunity.
            However, during severe <Glossary term="Market Crash" definition={glossaryTerms["Market Crash"]}>market crashes</Glossary>, the RSI can stay <Glossary term="Oversold" definition={glossaryTerms["Oversold"]}>"oversold"</Glossary> for extended periods.
          </p>
          
          {/* Interactive RSI Chart */}
          <div className="rsi-chart-container">
            <h5>Interactive RSI Chart - VOO (S&P 500 ETF)</h5>
            <p className="chart-description">
              Use this interactive chart to analyze RSI levels in real-time. The RSI indicator is the purple line displayed below the main price chart. 
              Look for RSI values below 30 (<Glossary term="Oversold" definition={glossaryTerms["Oversold"]}>oversold</Glossary>) or above 70 (<Glossary term="Overbought" definition={glossaryTerms["Overbought"]}>overbought</Glossary>) to identify potential trading opportunities.
            </p>
            <div className="rsi-chart-wrapper">
              <iframe 
                src={`${process.env.REACT_APP_GRAPHICS_URL}/rsiSignals/spx/`}
                title="S&P 500 RSI Trading Signals"
                className="performance-chart"
                frameBorder="0"
                loading="lazy"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
        
        <div 
          ref={el => sectionRefs.current.stocksAboveMA = el}
          className="indicator-box"
        >
          <h4>Percentage of Stocks Above <Glossary term="Moving Average" definition={glossaryTerms["Moving Average"]}>50-Day Moving Average</Glossary></h4>
          <p>
            Imagine a school of fish swimming in the ocean:
          </p>
          <ul>
            <li>The "50-day moving average" is like the average path each fish has taken over the last 50 days</li>
            <li>When most fish (stocks) swim above their average path, the ocean is likely calm or improving (<Glossary term="Market Recovery" definition={glossaryTerms["Market Recovery"]}>market recovery</Glossary>)</li>
            <li>When only 15% or fewer stocks are above their 50-day average, it often signals an <Glossary term="Oversold" definition={glossaryTerms["Oversold"]}>oversold market</Glossary></li>
            <li>Historically, when this percentage reaches extremely low levels and begins to improve (more stocks moving above their averages), it can indicate a good time to buy</li>
          </ul>
          <p>
            This indicator helps measure <Glossary term="Market Breadth" definition={glossaryTerms["Market Breadth"]}>market breadth</Glossary> and recovery momentum. When more stocks start rising above their moving averages after a decline, it suggests the market may be finding its footing and beginning to recover.
          </p>
          
          <div className="stocks-ma-chart-container">
            <h5>Interactive Stocks Above Moving Average Chart</h5>
            <p className="chart-description">
              Track the real-time percentage of stocks trading above their 50-day moving average. When this graph goes below 15, it marks an extreme <Glossary term="Oversold" definition={glossaryTerms["Oversold"]}>oversold</Glossary> level that historically signals potential buying opportunities.
            </p>
            <div className="stocks-ma-chart-wrapper">
              <StocksAboveMAWidget />
            </div>
          </div>
        </div>
        
        <div 
          ref={el => sectionRefs.current.standardDeviation = el}
          className="indicator-box"
        >
          <h4>Average S&P 500 Performance and <Glossary term="Standard Deviation" definition={glossaryTerms["Standard Deviation"]}>Standard Deviation</Glossary></h4>
          <p>
            Looking at the chart, you can see the blue line showing the average S&P 500 return throughout the year. The dotted lines represent standard deviations:
          </p>
          <ul>
            <li>When the current year's performance (red line) falls below the -1 or -2 standard deviation lines, it's statistically unusual</li>
            <li>These extreme deviations from normal patterns often don't last long</li>
            <li>When markets are performing much worse than their historical average, it may present a buying opportunity</li>
          </ul>
          <p>
            Think of standard deviations like a rubber band: the further it stretches from normal, the more likely it is to snap back.
            When stock prices fall far below their average trend (more than 1-2 standard deviations), they've historically tended to rebound.
          </p>
          
          <div className="strategies-chart-container">
            <h4>S&P 500 Historical Performance</h4>
            <div className="strategies-chart-wrapper">
              <iframe 
                src={`${process.env.REACT_APP_GRAPHICS_URL}/ytd/sp500performanceAllYears/`}
                title="S&P 500 Historical Performance"
                className="performance-chart"
                frameBorder="0"
                loading="lazy"
                allowFullScreen
              ></iframe>
            </div>
            <div className="strategies-chart-legend">
              <div className="legend-item">
                <span className="legend-color blue"></span>
                <span>Average S&P Return (All Years)</span>
              </div>
              <div className="legend-item">
                <span className="legend-color red"></span>
                <span>Current Year</span>
              </div>
              <div className="legend-item">
                <span className="legend-color dotted"></span>
                <span>Standard Deviation Bands</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      

    </div>
  );

  return (
    <>
      <LoggedInHeader />
      
      <div className="strategies-container">
        {/* Table of Contents - fixed sidebar on desktop, collapsible
            accordion under the header on mobile (wireframe 1g) */}
        <div className={`strategies-sidebar ${isTocMinimized ? 'minimized' : ''} ${isTocOpen ? 'ft-toc-open' : ''}`}>
          <button
            className="ft-toc-accordion-toggle"
            onClick={() => setIsTocOpen(!isTocOpen)}
            aria-expanded={isTocOpen}
          >
            <span>Table of Contents</span>
            {isTocOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          <div className="toc-container">
            <div className="toc-header">
              <h2 className="toc-title">Table of Contents</h2>
              <button
                className="toc-toggle-btn"
                onClick={toggleTocMinimized}
                aria-label={isTocMinimized ? "Expand table of contents" : "Minimize table of contents"}
              >
                {isTocMinimized ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
              </button>
            </div>

            {(!isTocMinimized || isMobile) && (
              <ul className="toc-list">
                <li className="toc-item">
                  <div 
                    className={`toc-link ${activeSection === "intro" ? "active" : ""}`}
                    onClick={() => scrollToSection("intro")}
                  >
                    Introduction
                  </div>
                </li>
                
                <li className="toc-item">
                  <div 
                    className={`toc-link ${activeSection === "investments" ? "active" : ""}`}
                    onClick={() => scrollToSection("investments")}
                  >
                    Investment Recommendations
                  </div>
                </li>
                
                <li className="toc-item">
                  <div className="toc-section-header">
                    <div className="toc-subtitle"><Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>Portfolio</Glossary> Strategies</div>
                    <button 
                      className="toc-section-toggle"
                      onClick={(e) => toggleSectionCollapsed(e, 'strategies')}
                      aria-label={collapsedSections.strategies ? "Expand strategies section" : "Collapse strategies section"}
                    >
                      {collapsedSections.strategies ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                  </div>
                  
                  <div className={`toc-section-content ${collapsedSections.strategies ? 'collapsed' : ''}`}>
                    {strategies.map((strategy) => (
                      <div 
                        key={`toc-${strategy.id}`}
                        className={`toc-link ${activeSection === `strategy-${strategy.id}` ? "active" : ""}`}
                        onClick={() => scrollToSection(`strategy-${strategy.id}`)}
                      >
                        {strategy.title}
                      </div>
                    ))}
                  </div>
                </li>
                
                <li className="toc-item">
                  <div 
                    className={`toc-link ${activeSection === "rebalancing" ? "active" : ""}`}
                    onClick={() => scrollToSection("rebalancing")}
                  >
                    Rebalancing Explanation
                  </div>
                </li>
                
                <li className="toc-item">
                  <div className="toc-section-header">
                    <div className="toc-subtitle">How to Buy & Sell</div>
                    <button 
                      className="toc-section-toggle"
                      onClick={(e) => toggleSectionCollapsed(e, 'orderTypes')}
                      aria-label={collapsedSections.orderTypes ? "Expand order types section" : "Collapse order types section"}
                    >
                      {collapsedSections.orderTypes ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                  </div>
                  
                  <div className={`toc-section-content ${collapsedSections.orderTypes ? 'collapsed' : ''}`}>
                    <div 
                      className={`toc-link ${activeSection === "marketOrder" ? "active" : ""}`}
                      onClick={() => scrollToSection("marketOrder")}
                    >
                      Market Order
                    </div>
                    <div 
                      className={`toc-link ${activeSection === "limitOrder" ? "active" : ""}`}
                      onClick={() => scrollToSection("limitOrder")}
                    >
                      Limit Order
                    </div>
                    <div 
                      className={`toc-link ${activeSection === "stopLimitOrder" ? "active" : ""}`}
                      onClick={() => scrollToSection("stopLimitOrder")}
                    >
                      Stop Limit Order
                    </div>
                  </div>
                </li>

                <li className="toc-item">
                  <div className="toc-section-header">
                    <div className="toc-subtitle">Buy Low, Sell High</div>
                    <button 
                      className="toc-section-toggle"
                      onClick={(e) => toggleSectionCollapsed(e, 'buyLowSellHigh')}
                      aria-label={collapsedSections.buyLowSellHigh ? "Expand buy low sell high section" : "Collapse buy low sell high section"}
                    >
                      {collapsedSections.buyLowSellHigh ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                  </div>
                  
                  <div className={`toc-section-content ${collapsedSections.buyLowSellHigh ? 'collapsed' : ''}`}>
                    <div 
                      className={`toc-link ${activeSection === "rsiIndicator" ? "active" : ""}`}
                      onClick={() => scrollToSection("rsiIndicator")}
                    >
                      RSI Indicator
                    </div>
                    <div 
                      className={`toc-link ${activeSection === "stocksAboveMA" ? "active" : ""}`}
                      onClick={() => scrollToSection("stocksAboveMA")}
                    >
                      Stocks Above Moving Average
                    </div>
                    <div 
                      className={`toc-link ${activeSection === "standardDeviation" ? "active" : ""}`}
                      onClick={() => scrollToSection("standardDeviation")}
                    >
                      Standard Deviation Analysis
                    </div>
                  </div>
                </li>

                <li className="toc-item">
                  <div 
                    className={`toc-link ${activeSection === "dollarCostAveraging" ? "active" : ""}`}
                    onClick={() => scrollToSection("dollarCostAveraging")}
                  >
                    Dollar Cost Averaging
                  </div>
                </li>
              </ul>
            )}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="strategies-content">
          {/* Introduction Section */}
          <div 
            ref={el => sectionRefs.current.intro = el} 
            className="strategy-section"
          >
            <div className="warning-banner">
              <p>Before investing, consider these key principles: Only invest money you won't need for at least 18 months. 
                Never risk more than you can afford to lose. 
                The best investors maintain enough uninvested cash to buy more stocks when the market goes down.</p>
            </div>
            <hr />
            <div className="practical-tips">
        <h3>Practical Tips for Average Investors</h3>
          <ul>
            <li><strong>Don't try to pick the exact bottom.</strong> Instead, consider gradually buying more when markets are significantly down.</li>
            <li><strong>Keep cash reserves.</strong> Having money available to invest during downturns is crucial for this strategy.</li>
            <li><strong>Consider using <Glossary term="Dollar-Cost Averaging" definition={glossaryTerms["Dollar-Cost Averaging"]}>dollar-cost averaging</Glossary>.</strong> Invest fixed amounts regularly, but perhaps invest slightly more during major market declines.</li>
            <li><strong>Focus on quality.</strong> During market bottoms, even excellent companies get sold off. Look for financially strong businesses with good long-term prospects.</li>
            <li><strong>Be patient.</strong> <Glossary term="Market Recovery" definition={glossaryTerms["Market Recovery"]}>Market recoveries</Glossary> can take time, and there might be several "false bottoms" before the actual turnaround.</li>
          </ul>
        </div>

        <div className="bottom-note">
        <p>
          Remember: The most successful investors manage their emotions and see market declines as potential opportunities rather than reasons to panic. 
        </p>
      </div>
      <hr />
          </div>
          
          {/* Investment Recommendations Section */}
          <div 
            ref={el => sectionRefs.current.investments = el} 
            className="strategy-section"
          >
            <h1 className="strategy-header">Investment Recommendations</h1>
            <div className="investments-table-container">
              <table className="investments-table">
                <thead>
                  <tr>
                    <th><Glossary term="Ticker" definition={glossaryTerms["Ticker"]}>Ticker</Glossary></th>
                    <th>Name</th>
                    <th><Glossary term="Expense Ratio" definition={glossaryTerms["Expense Ratio"]}>Expense Ratio</Glossary></th>
                    <th>What is it?</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((investment, index) => (
                    <tr key={index}>
                      <td className="ticker-cell" data-label="Ticker">{investment.ticker}</td>
                      <td data-label="Name">{investment.name}</td>
                      <td className="expense-cell" data-label="Expense Ratio">{investment.expense}</td>
                      <td data-label="What is it?">
                        {investment.ticker === "VOO" ? (
                          <>Owns shares in 500 of America's largest companies like Apple, Microsoft, and Amazon</>
                        ) : investment.ticker === "VT" ? (
                          <>Owns pieces of nearly every <Glossary term="Public Company" definition={glossaryTerms["Public Company"]}>public company</Glossary> on Earth - over 9,000 companies worldwide</>
                        ) : investment.ticker === "QQQ" ? (
                          <>Focuses on 100 of the largest non-financial companies, with a heavy focus on technology</>
                        ) : investment.ticker === "BRK.B" ? (
                          <><Glossary term="Warren Buffett" definition={glossaryTerms["Warren Buffett"]}>Warren Buffett's</Glossary> company that owns many businesses and invests in other companies</>
                        ) : investment.ticker === "VGTSX" ? (
                          <>Owns companies from developed and emerging markets outside the U.S.</>
                        ) : investment.ticker === "MUB" ? (
                          <>Holds tax-exempt <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary> issued by states, cities, and counties across the U.S.</>
                        ) : investment.ticker === "BND" ? (
                          <>Holds thousands of U.S. government and corporate <Glossary term="Bond" definition={glossaryTerms["Bond"]}>bonds</Glossary> of different lengths</>
                        ) : investment.ticker === "FBTC" ? (
                          <>Tracks the price of <Glossary term="Bitcoin" definition={glossaryTerms["Bitcoin"]}>Bitcoin</Glossary> without needing a crypto wallet</>
                        ) : investment.ticker === "FETH" ? (
                          <>Tracks the price of <Glossary term="Ethereum" definition={glossaryTerms["Ethereum"]}>Ethereum</Glossary> without needing a crypto wallet</>
                        ) : investment.ticker === "N/A" ? (
                          <>Cash management account paying 4% interest on uninvested cash (requires Gold subscription)</>
                        ) : (
                          investment.description
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <hr />
          </div>
          
          <h1 className="strategy-header"><Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>Portfolio</Glossary> Allocations</h1>
          
          {/* Strategy Sections */}
          {strategies.map((strategy) => (
            <div 
              key={strategy.id} 
              ref={el => sectionRefs.current[`strategy-${strategy.id}`] = el} 
              className="strategy-section"
            >
              <div className="comparison-header">
                <h2 className="comparison-title">{strategy.title}</h2>
                <div className="image-container">
                  <img 
                    src={strategy.image} 
                    alt={strategy.title} 
                    className="strategy-image"
                  />
                </div>
                <div className="account-summary">
                  <div className="account-type">
                    <h3>Pros</h3>
                    <ul>
                      {strategy.pros.map((pro, index) => (
                        <li key={index}>{pro}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="vs-divider"></div>
                  <div className="account-type">
                    <h3>Cons</h3>
                    <ul>
                      {strategy.renderCons ? (
                        strategy.renderCons().map((con, index) => (
                          <li key={index}>{con}</li>
                        ))
                      ) : (
                        strategy.cons.map((con, index) => (
                          <li key={index}>{con}</li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
                <div className="explanation-section">
                  <h3>Explanation</h3>
                  {strategy.renderExplanation ? (
                    <p>{strategy.renderExplanation()}</p>
                  ) : (
                    <p>{strategy.explanation}</p>
                  )}
                </div>
              </div>
              <hr />
            </div>
          ))}
          
          
          {/* Rebalancing Section */}
          <RebalancingExplanation />
          
          {/* Order Types Section */}
          <div 
            ref={el => sectionRefs.current.orderTypes = el} 
            className="strategy-section"
          >
            <hr />
            <h1 className="strategy-header">How to Buy & Sell</h1>
            
            <div className="order-types-container">
              <div 
                ref={el => sectionRefs.current.marketOrder = el}
                className="order-type"
              >
                <h3 className="order-title">Market Order</h3>
                <div className="order-description">
                  <p>
                    A <Glossary term="Market Order" definition={glossaryTerms["Market Order"]}>market order</Glossary> is like saying "I'll take it at whatever price it's selling for right now."
                  </p>
                  <div className="order-details">
                    <h4>When to use it:</h4>
                    <ul>
                      <li>When you want to buy or sell immediately</li>
                      <li>When the exact price isn't your main concern</li>
                      <li>For very widely traded investments where prices don't change much between seconds</li>
                    </ul>
                    <h4>Example:</h4>
                    <p>
                      If Apple stock is trading around $175, and you place a market order to buy, you'll get 
                      it at whatever the current asking price is (maybe $175.05). The order completes nearly instantly.
                    </p>
                  </div>
                </div>
              </div>
              
              <div 
                ref={el => sectionRefs.current.limitOrder = el}
                className="order-type"
              >
                <h3 className="order-title">Limit Order</h3>
                <div className="order-description">
                  <p>
                    A <Glossary term="Limit Order" definition={glossaryTerms["Limit Order"]}>limit order</Glossary> is like saying "I'll only buy if the price is at or below $X" or "I'll only sell if the price is at or above $X."
                  </p>
                  <div className="order-details">
                    <h4>When to use it:</h4>
                    <ul>
                      <li>When you have a specific price in mind</li>
                      <li>When you're not in a rush to buy or sell</li>
                      <li>When you want to make sure you don't pay more (or sell for less) than a certain amount</li>
                      <li><strong>During big market moves</strong> (up or down), when the "<Glossary term="Spread" definition={glossaryTerms["Spread"]}>spread</Glossary>" between buy and sell prices gets wider</li>
                    </ul>
                    <h4>Example:</h4>
                    <p>
                      If Apple stock is trading at $175, you might place a limit order to buy at $170. 
                      The order will only execute if the price drops to $170 or lower. If the price never 
                      reaches $170, the order doesn't execute.
                    </p>
                  </div>
                </div>
              </div>
              
              <div 
                ref={el => sectionRefs.current.stopLimitOrder = el}
                className="order-type"
              >
                <h3 className="order-title">Stop Limit Order</h3>
                <div className="order-description">
                  <p>
                    A <Glossary term="Stop Limit Order" definition={glossaryTerms["Stop Limit Order"]}>stop limit order</Glossary> is like saying "If the price reaches $X (the stop price), place a limit order at $Y (the limit price)."
                  </p>
                  <div className="order-details">
                    <h4>When to use it:</h4>
                    <ul>
                      <li>To protect profits on investments you own (setting a sell stop limit below current price)</li>
                      <li>To buy only when a stock shows upward momentum (setting a buy stop limit above current price)</li>
                      <li>When you want more control over entry and exit prices</li>
                    </ul>
                    <h4>Example:</h4>
                    <p>
                      You own Apple at $150 and it's now $175. You might set a stop limit order with a stop price of $165 and 
                      a limit price of $160. If Apple drops to $165, a limit order to sell at $160 or higher is activated. 
                      This protects most of your profit if the stock falls suddenly.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="order-type-tips">
                <h3>Tips for Placing Orders:</h3>
                <ul>
                  <li>For most long-term investors buying broad <Glossary term="Index Fund" definition={glossaryTerms["Index Fund"]}>index funds</Glossary>, simple market orders work fine</li>
                  <li>Limit orders are useful when buying or selling less frequently traded investments</li>
                  <li>When markets are moving rapidly (either crashing or surging), consider using limit orders instead of market orders - the difference between buy and sell prices (the <Glossary term="Spread" definition={glossaryTerms["Spread"]}>spread</Glossary>) gets larger during these times</li>
                  <li>Stop limit orders are more advanced - practice with small amounts first</li>
                  <li>Remember that limit and stop limit orders might not execute if the price doesn't reach your specified level</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Buy Low Sell High Section */}
          <BuyLowSellHighSection />

          {/* Dollar Cost Averaging Section */}
          <div 
            ref={el => sectionRefs.current.dollarCostAveraging = el} 
            className="strategy-section"
          >
            <hr />
            <h1 className="strategy-header">Dollar Cost Averaging</h1>
            
            <div className="quote-box">
              <blockquote>
                "Time in the market beats timing the market."
                <cite>- Common Investment Wisdom</cite>
              </blockquote>
            </div>
            
            <h3>What is <Glossary term="Dollar-Cost Averaging" definition={glossaryTerms["Dollar-Cost Averaging"]}>Dollar Cost Averaging</Glossary>?</h3>
            <p>
              Imagine you want to buy apples from a farmer, but apple prices change every week. Some weeks apples cost $2 per pound, 
              other weeks they cost $4 per pound. Instead of trying to guess when apples are cheapest, you decide to buy $20 worth 
              of apples every single week, no matter what the price is.
            </p>
            
            <p>
              <Glossary term="Dollar-Cost Averaging" definition={glossaryTerms["Dollar-Cost Averaging"]}>Dollar cost averaging</Glossary> works the same way with investments. 
              You invest the same amount of money regularly (like $100 every month) regardless of whether stock prices are high or low.
            </p>
            
            <div className="practical-example">
              <h3>Simple Example: The Coffee Shop Analogy</h3>
              <p>
                Let's say you spend $50 every month at your favorite coffee shop, but coffee prices fluctuate:
              </p>
              
              <div className="example-table">
                <table className="dca-example-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Coffee Price</th>
                      <th>Money Spent</th>
                      <th>Cups Bought</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>January</td>
                      <td>$5 per cup</td>
                      <td>$50</td>
                      <td>10 cups</td>
                    </tr>
                    <tr>
                      <td>February</td>
                      <td>$2.50 per cup</td>
                      <td>$50</td>
                      <td>20 cups</td>
                    </tr>
                    <tr>
                      <td>March</td>
                      <td>$10 per cup</td>
                      <td>$50</td>
                      <td>5 cups</td>
                    </tr>
                    <tr>
                      <td>April</td>
                      <td>$2 per cup</td>
                      <td>$50</td>
                      <td>25 cups</td>
                    </tr>
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td><strong>Average: $4.88</strong></td>
                      <td><strong>$200</strong></td>
                      <td><strong>60 cups</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p>
                Notice what happened: When coffee was expensive ($10), you automatically bought fewer cups (5). 
                When coffee was cheap ($2), you automatically bought more cups (25). You spent the same amount each month, 
                but you naturally bought more when prices were low and less when prices were high.
              </p>
            </div>
            
            <div className="dca-benefits">
              <h3>Why Does This Work?</h3>
              <div className="benefit-explanation">
                <h4><span role="img" aria-label="target">🎯</span> Automatic "Buy Low" Behavior</h4>
                <p>
                  When stock prices drop, your regular $100 investment buys more shares. When prices are high, 
                  the same $100 buys fewer shares. You're essentially training yourself to buy more when things are "on sale."
                </p>
                
                <h4><span role="img" aria-label="relieved face">😌</span> Removes Emotion</h4>
                <p>
                  You don't have to decide if today is a "good day" to invest. You just invest the same amount regularly, 
                  like paying a monthly bill. This prevents you from making emotional decisions based on scary news headlines.
                </p>
                
                <h4><span role="img" aria-label="clock">⏰</span> Smooths Out the Bumps</h4>
                <p>
                  Stock prices go up and down like a roller coaster. Dollar cost averaging is like taking the average 
                  of all those ups and downs over time, which usually results in a smoother, more predictable outcome.
                </p>
              </div>
            </div>
            
            <div className="real-world-application">
              <h3>How to Use Dollar Cost Averaging</h3>
              <div className="application-steps">
                <h4>Step 1: Pick Your Amount</h4>
                <p>
                  Decide how much you can comfortably invest every month. It could be $25, $100, $500, or any amount that 
                  fits your budget. The key is consistency, not the size of the amount.
                </p>
                
                <h4>Step 2: Pick Your Schedule</h4>
                <p>
                  Choose when you'll invest: every week, every two weeks, or every month. Most people choose monthly 
                  because it aligns with their paycheck.
                </p>
                
                <h4>Step 3: Pick Your Investment</h4>
                <p>
                  Choose one of the several <Glossary term="Portfolio" definition={glossaryTerms["Portfolio"]}>portfolios</Glossary> shown in the "Portfolio" strategies section. Once you've decided on one,
                  consistently invest the same amount each month in the chosen <Glossary term="Allocation" definition={glossaryTerms["Allocation"]}>allocation</Glossary>.
                </p>
                
                <h4>Step 4: Automate It</h4>
                <p>
                  Set up automatic transfers from your bank account to your investment account. This way, you don't have to 
                  remember to invest each month - it happens automatically like any other bill.
                </p>
              </div>
            </div>
            
            <div className="dca-considerations">
              <h3>Important Things to Remember</h3>
              <ul>
                <li><strong>It's not magic:</strong> Dollar cost averaging doesn't guarantee you'll make money, but it does help reduce the impact of bad timing</li>
                <li><strong>Keep it simple:</strong> Don't try to "improve" the strategy by skipping months or changing amounts based on market conditions</li>
                <li><strong>Think long-term:</strong> This strategy works best over years, not weeks or months</li>
                <li><strong>Stay consistent:</strong> The power comes from sticking to the plan, even when the news is scary or exciting</li>
                <li><strong>Don't stress about perfection:</strong> Even if you miss a month or two, just get back on track when you can</li>
              </ul>
            </div>
            
            <div className="bottom-note">
              <p>
                <strong>Bottom line:</strong> Dollar cost averaging is like being a steady, patient tortoise instead of a 
                frantic rabbit trying to time the perfect moment. The tortoise usually wins the race.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add ReactTooltip for glossary terms */}
      <ReactTooltip />
    </>
  );
};

export default Strategies;